import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Card,
  Avatar,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export interface AssessmentModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  duration: string;
  meetingSummary: string;
  goals: string[];
  companyName: string;
  companyIndustry: string;
  assignmentId?: number;
  personaName: string;
  personaRole: string;
  companyLocations: string;
  onStart?: () => void;
  assessmentId?: string;
  completeStatus: boolean
  isAvailable: boolean
}

const AssessmentModal: React.FC<AssessmentModalProps> = ({
  open,
  onClose,
  title,
  duration,
  meetingSummary,
  goals,
  companyName,
  companyIndustry,
  assignmentId,
  personaName,
  personaRole,
  companyLocations,
  onStart,
  assessmentId,
  completeStatus,
  isAvailable,
}) => {
  const navigate = useNavigate();

  const handleStart = () => {
    if (onStart) {
      onStart();
    } else if (title) {
      if (!Number.isFinite(assignmentId) || Number(assignmentId) <= 0) {
        return;
      }
      navigate(`/call/${assessmentId}`, {
        state: {
          assignmentId,
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Meeting Briefing
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Scenario: {title}
          </Typography>
          <Chip
            icon={<TimeIcon sx={{ fontSize: 18 }}/>}
            label={`Meeting Length: ~${duration}`}
            color="default"
            size="small"
            sx={{ 
              fontWeight: 500,
              background: 'linear-gradient(45deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
              color: 'white'
             }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Meeting Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {meetingSummary}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Meeting Goals
        </Typography>
        <List>
          {goals.map((goal) => (
            <ListItem key={goal} disableGutters sx={{ alignItems: 'flex-start' }}>
              <ListItemIcon>
                <CheckIcon color="success" sx={{ mt: '4px' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {goal}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
        {(
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Customer Details
            </Typography>
            <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 2, boxShadow: 1 }}>
              <Box sx={{ pr: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 1 }}>
                {/* ToDO */}
                <Avatar
                  // src={customerDetails.companyLogoUrl}
                  alt="Company Logo"
                  sx={{ width: 48, height: 48, borderRadius: 2, mr: 2 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {companyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {companyIndustry}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ ml: 6 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {personaName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {personaRole}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Location: {companyLocations}
                </Typography>
              </Box>
            </Card>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" sx={{         
          borderColor: 'rgba(255,0,192,1)',
          color: 'rgba(255,0,192,1)',
        }}>
          Close
        </Button>
        <Button
          onClick={handleStart}
          variant="contained"
          autoFocus
          sx={{         
            background: 'linear-gradient(45deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
            color: 'white'
          }}
          disabled={!isAvailable}
        >
          {!isAvailable
            ? "Assessment Not Available"
            : completeStatus
              ? "Retake Assessment"
              : "Start Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentModal;